import React, { useContext, useMemo } from "react";
import { Drawer } from "@design-system/Drawer";
import { Separator } from "@design-system/Separator/Separator";
import { DocumentName } from "@components/documents/DocumentName";
import { ImporterPreviewNavigation } from "@components/importer/ImporterPreviewNavigation";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import importerStyles from "@components/importer/ImporterPage.module.scss";
import { AccrualImporterContext } from "./accrualImporter.context";
import { AccrualDocumentReviewDrawerContent } from "./AccrualDocumentReviewDrawerContent";
import { useAccrualPreviewNavigation } from "./useAccrualPreviewNavigation";
import styles from "./AccrualDocumentImporter.module.scss";

export const AccrualDocumentReviewDrawer: React.FC = () => {
    const { mode, sortedRows, previewedRowId, setPreviewedRowId } = useContext(
        AccrualImporterContext,
    );

    const { previewedIndex, totalCount, previewNext, previewPrevious } =
        useAccrualPreviewNavigation(sortedRows);

    const previewedRow = useMemo(
        () => sortedRows.find((row) => row.id === previewedRowId),
        [sortedRows, previewedRowId],
    );

    const previewedFinancialDocumentId = previewedRow?.financialDocument?.id;

    useKeyboardCommand({
        keys: "Escape",
        callback: () => setPreviewedRowId(undefined),
        options: {
            enabled: !!previewedRow,
            preventDefault: true,
            description: "Close document preview",
        },
    });

    const drawerBody = (() => {
        if (previewedFinancialDocumentId) {
            return (
                <AccrualDocumentReviewDrawerContent
                    key={previewedFinancialDocumentId}
                    financialDocumentId={previewedFinancialDocumentId}
                    mode={mode}
                />
            );
        }

        if (previewedRow) {
            return (
                <div className={importerStyles.drawerContent}>
                    <p className={styles.unavailableMessage}>
                        This document cannot be reviewed yet.
                    </p>
                </div>
            );
        }

        return null;
    })();

    return (
        <Drawer
            open={!!previewedRow}
            direction="right"
            modal={false}
            size="fullScreen"
            dismissible={false}
            headerClassName={importerStyles.drawerHeader}
            headerAddonLeft={
                previewedRow?.financialDocument ? (
                    <DocumentName
                        fileName={previewedRow.financialDocument.fileName}
                        icon
                        documentId={previewedRow.financialDocument.id}
                        textClassName={importerStyles.drawerDocumentName}
                    />
                ) : null
            }
            headerAddonBeforeClose={
                previewedRow ? (
                    <div className={importerStyles.headerRight}>
                        <ImporterPreviewNavigation
                            onNext={previewNext}
                            onPrevious={previewPrevious}
                            currentIndex={previewedIndex + 1}
                            totalCount={totalCount}
                        />
                        <Separator
                            orientation="vertical"
                            className={importerStyles.headerSeparator}
                        />
                    </div>
                ) : null
            }
            onClose={() => setPreviewedRowId(undefined)}
            noPadding
        >
            {drawerBody}
        </Drawer>
    );
};
